import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import type {
    IaConversaDTO,
    IaMensagemDTO,
    IaOperacaoDTO,
    IaPerguntaDTO,
    IaStreamEventDTO,
} from '@webip/shared/src/dto/ia';
import type { ImagemPreparada } from './imagemChat';

/**
 * Estado e transporte do assistente de configuração.
 *
 * O envio de mensagem usa `fetch` + `ReadableStream` em vez do axios do resto do
 * app: a resposta é um stream SSE, e o XHR do axios só entrega o corpo inteiro
 * no fim — o texto não apareceria conforme é gerado.
 */

export type AssistenteEstado = {
    conversa: IaConversaDTO | null;
    mensagens: IaMensagemDTO[];
    /** Texto do turno em andamento, ainda sendo transmitido. */
    parcial: string;
    operacoes: IaOperacaoDTO[];
    pergunta: IaPerguntaDTO | null;
    ferramentaAtual: string | null;
    enviando: boolean;
    erro: string | null;
};

/** Intervalo de consulta de uma conversa com turno em andamento no backend. */
const INTERVALO_CONSULTA_MS = 3000;

const ESTADO_INICIAL: AssistenteEstado = {
    conversa: null,
    mensagens: [],
    parcial: '',
    operacoes: [],
    pergunta: null,
    ferramentaAtual: null,
    enviando: false,
    erro: null,
};

/**
 * Só diz se a feature está configurada no backend. Fica separado de
 * `useAssistente` para que o layout possa decidir se mostra o botão sem montar
 * o estado inteiro da conversa.
 */
export function useIaHabilitada(): boolean | null {
    const [habilitada, setHabilitada] = useState<boolean | null>(null);

    useEffect(() => {
        let cancelado = false;
        axios
            .get('/api/ia/status', { withCredentials: true })
            .then((res) => {
                if (!cancelado) setHabilitada(!!res.data?.habilitada);
            })
            .catch(() => {
                if (!cancelado) setHabilitada(false);
            });
        return () => {
            cancelado = true;
        };
    }, []);

    return habilitada;
}

export function useAssistente(applianceId: string | null) {
    const [estado, setEstado] = useState<AssistenteEstado>(ESTADO_INICIAL);
    const abortRef = useRef<AbortController | null>(null);

    // Trocar de appliance começa uma conversa nova: o contexto do assistente é
    // montado a partir dela na primeira mensagem.
    useEffect(() => {
        abortRef.current?.abort();
        setEstado(ESTADO_INICIAL);
    }, [applianceId]);

    const reiniciar = useCallback(() => {
        abortRef.current?.abort();
        setEstado(ESTADO_INICIAL);
    }, []);

    /** Troca a conversa ativa por uma do histórico. */
    const carregarConversa = useCallback(async (id: number) => {
        abortRef.current?.abort();

        const res = await axios.get(`/api/ia/conversa/${id}`, { withCredentials: true });
        const conversa = res.data?.result as IaConversaDTO;

        setEstado({
            ...ESTADO_INICIAL,
            conversa,
            mensagens: conversa.mensagens ?? [],
            operacoes: conversa.operacoes ?? [],
            pergunta: conversa.pergunta_pendente ?? null,
        });
    }, []);

    // Conversa aberta com um turno ainda rodando no backend (a página foi
    // recarregada, ou a conversa veio do histórico no meio de uma resposta): o
    // stream que a acompanhava morreu com a página, mas o backend segue e
    // grava cada volta. Consulta até a marca cair. Não roda enquanto esta
    // própria aba está no stream — aí os eventos já chegam por ele.
    const conversaId = estado.conversa?.id;
    const processandoNoServidor = !!estado.conversa?.processando && !estado.enviando;
    useEffect(() => {
        if (!conversaId || !processandoNoServidor) return;

        const timer = setInterval(async () => {
            try {
                const res = await axios.get(`/api/ia/conversa/${conversaId}`, { withCredentials: true });
                const conversa = res.data?.result as IaConversaDTO;
                setEstado((e) =>
                    e.conversa?.id !== conversaId
                        ? e
                        : {
                              ...e,
                              conversa,
                              mensagens: conversa.mensagens ?? e.mensagens,
                              operacoes: conversa.operacoes ?? e.operacoes,
                              pergunta: conversa.pergunta_pendente ?? null,
                          },
                );
            } catch {
                // Falha pontual de rede: a próxima volta tenta de novo.
            }
        }, INTERVALO_CONSULTA_MS);
        return () => clearInterval(timer);
    }, [conversaId, processandoNoServidor]);

    /** Cria a conversa se ainda não existe e devolve o id. */
    const garantirConversa = useCallback(async (): Promise<number> => {
        if (estado.conversa?.id) return estado.conversa.id;

        const res = await axios.post(
            '/api/ia/conversa',
            { appliance_id: applianceId ? Number(applianceId) : null },
            { withCredentials: true },
        );
        const conversa = res.data?.result as IaConversaDTO;
        setEstado((e) => ({ ...e, conversa }));
        return conversa.id!;
    }, [estado.conversa?.id, applianceId]);

    /**
     * Envia uma mensagem; `imagem` é opcional (print, foto, diagrama). Com
     * imagem o corpo vai como `multipart/form-data` — o JSON do backend tem
     * limite de 100 kb, e o base64 de um print não cabe nele.
     */
    const enviar = useCallback(
        async (texto: string, imagem?: ImagemPreparada | null) => {
            if ((!texto.trim() && !imagem) || estado.enviando || estado.conversa?.processando) return;

            const conversaId = await garantirConversa();

            const controller = new AbortController();
            abortRef.current = controller;

            setEstado((e) => ({
                ...e,
                enviando: true,
                erro: null,
                parcial: '',
                pergunta: null,
                mensagens: [
                    ...e.mensagens,
                    { papel: 'usuario', texto, imagens: imagem ? [imagem.dataUrl] : undefined },
                ],
            }));

            try {
                let corpo: BodyInit;
                const headers: Record<string, string> = {};
                if (imagem) {
                    const form = new FormData();
                    form.append('texto', texto);
                    form.append('imagem', imagem.blob, imagem.nome);
                    corpo = form;
                } else {
                    headers['Content-Type'] = 'application/json';
                    corpo = JSON.stringify({ texto });
                }

                const resposta = await fetch(`/api/ia/conversa/${conversaId}/mensagem`, {
                    method: 'POST',
                    credentials: 'include',
                    headers,
                    body: corpo,
                    signal: controller.signal,
                });

                if (!resposta.ok || !resposta.body) {
                    const corpo = await resposta.json().catch(() => null);
                    throw new Error(corpo?.msg ?? 'Falha ao falar com o assistente');
                }

                await consumirSSE(resposta.body, (evento) => aplicarEvento(setEstado, evento));
            } catch (err) {
                if ((err as Error).name === 'AbortError') return;
                setEstado((e) => ({
                    ...e,
                    erro: err instanceof Error ? err.message : 'Falha inesperada',
                }));
            } finally {
                setEstado((e) => ({ ...e, enviando: false, ferramentaAtual: null }));
            }
        },
        [estado.enviando, estado.conversa?.processando, garantirConversa],
    );

    /** Responde a uma pergunta com opções — mesmo caminho de uma mensagem livre. */
    const responder = useCallback((valores: string[]) => enviar(valores.join(', ')), [enviar]);

    const aplicar = useCallback(
        async (segredos: Record<string, Record<string, string>>) => {
            const conversaId = estado.conversa?.id;
            if (!conversaId) return null;

            try {
                const res = await axios.post(
                    `/api/ia/conversa/${conversaId}/aplicar`,
                    { segredos },
                    { withCredentials: true },
                );
                setEstado((e) => ({ ...e, operacoes: res.data?.operacoes ?? e.operacoes }));
                return res.data;
            } catch (err: any) {
                const dados = err?.response?.data;
                if (dados?.operacoes) {
                    setEstado((e) => ({ ...e, operacoes: dados.operacoes }));
                }
                return dados ?? { ok: false, erro: 'Falha ao aplicar o plano' };
            }
        },
        [estado.conversa?.id],
    );

    const descartar = useCallback(async () => {
        const conversaId = estado.conversa?.id;
        if (!conversaId) return;

        const res = await axios.post(
            `/api/ia/conversa/${conversaId}/descartar`,
            {},
            { withCredentials: true },
        );
        setEstado((e) => ({ ...e, operacoes: res.data?.operacoes ?? [] }));
    }, [estado.conversa?.id]);

    useEffect(() => () => abortRef.current?.abort(), []);

    /** Há resposta a caminho — por este stream ou por um turno órfão de reload. */
    const ocupado = estado.enviando || !!estado.conversa?.processando;

    return { estado, ocupado, enviar, responder, aplicar, descartar, reiniciar, carregarConversa };
}

function aplicarEvento(
    setEstado: React.Dispatch<React.SetStateAction<AssistenteEstado>>,
    evento: IaStreamEventDTO,
) {
    setEstado((e) => {
        switch (evento.tipo) {
            case 'texto':
                return { ...e, parcial: e.parcial + evento.texto, ferramentaAtual: null };
            case 'ferramenta':
                return { ...e, ferramentaAtual: evento.nome };
            case 'pergunta':
                return { ...e, pergunta: evento.pergunta, ferramentaAtual: null };
            case 'plano':
                return { ...e, operacoes: evento.operacoes };
            case 'fim':
                // O histórico consolidado do servidor substitui o parcial.
                return {
                    ...e,
                    conversa: evento.conversa,
                    mensagens: evento.conversa.mensagens ?? e.mensagens,
                    operacoes: evento.conversa.operacoes ?? e.operacoes,
                    pergunta: evento.conversa.pergunta_pendente ?? null,
                    parcial: '',
                    ferramentaAtual: null,
                };
            case 'erro':
                return { ...e, erro: evento.mensagem, ferramentaAtual: null };
            default:
                return e;
        }
    });
}

/** Lê um corpo `text/event-stream` e entrega um evento por bloco `data:`. */
async function consumirSSE(
    body: ReadableStream<Uint8Array>,
    onEvento: (evento: IaStreamEventDTO) => void,
) {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    for (;;) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Um evento por bloco separado por linha em branco; o último pedaço pode
        // estar incompleto e fica no buffer para a próxima leitura.
        const blocos = buffer.split('\n\n');
        buffer = blocos.pop() ?? '';

        for (const bloco of blocos) {
            const linha = bloco.split('\n').find((l) => l.startsWith('data: '));
            if (!linha) continue;
            try {
                onEvento(JSON.parse(linha.slice(6)) as IaStreamEventDTO);
            } catch {
                // Bloco malformado: ignorar em vez de derrubar o stream inteiro.
            }
        }
    }
}
